import { Formik } from "formik";
import { rem } from "polished";
import React from "react";
import styled from "styled-components";
import { Yup } from "csv-package";
import ReactGA from "react-ga";

import QuestionsFrame from "../../../components/QuestionsFrame";
import { useLocation } from "react-router-dom";
import { isAuthenticated } from "../../../helpers/auth";
import ApplicationCreate from "../../../pages/ApplicationCreate";
import ApplicationCreateAnonymous from "../../../pages/ApplicationCreateAnonymous";
import Info from "../../../components/Info";
import StandaloneRadioGroup from "../../../components/StandaloneRadioGroup";
import ButtonContainer from "../../../components/ButtonContainer";
import Button from "../../../components/Button";
import InfoNote from "../../../components/InfoNote";
import colors from "../../../theme/colors";

const validation = {
  step: Yup.number().required(),
  seeking: Yup.string().when("step", {
    is: 1,
    then: Yup.string()
      .equals(
        ["appl"],
        "You cannot use this form if you are the respondent to an intervention order."
      )
      .required("Please select the role that best describes you"),
  }),
  appl_info_share: Yup.string().when("step", {
    is: 2,
    then: Yup.string().required(
      "One of the options must be selected to proceed"
    ),
  }),
};

const Gatekeeper = () => {
  const { pathname } = useLocation();
  const [authenticated, setAuthenticated] = React.useState<boolean | null>(
    null
  );

  const infoShareRef = React.useRef<string>("");
  const [progress, setProgress] = React.useState<boolean>(false);

  React.useEffect(() => {
    ReactGA.pageview(pathname);

    const checkAuth = async () => {
      const userIsAuthenticated = await isAuthenticated();
      setAuthenticated(userIsAuthenticated);
    };

    checkAuth();
  }, [pathname]);

  // create application if everything passes
  if (progress && authenticated) {
    return <ApplicationCreate infoShare={infoShareRef.current} />;
  }
  if (progress && !authenticated) {
    return <ApplicationCreateAnonymous infoShare={infoShareRef.current} />;
  }

  const shareOption1 = 
    `<p>We may share information about your Family Violence Intervention Order (FVIO) and any family violence you’ve told us about.</p>
    <p>This could include:</p>
    <ul><li>What you or the Police wrote in your FVIO application</li><li>Who is protected by the order</li><li>The conditions of the order</li><li>When a hearing is happening</li></ul>
    <p>We only share this information if it helps to reduce the risk or impact of family violence for you or your family.</p>`;
  const shareOption2 =
    `<p>We will only share your identifying information like your name, contact details, date of birth, and demographic details (for example, your age or culture).</p>
    <p>We won’t share anything that talks about your experience of family violence. For example, we won’t share what you or the Police wrote in your FVIO application.</p>
    <p>We might still share:</p>
    <ul><li>A copy of the FVIO (including the conditions)</li><li>The hearing date</li><li>Who is included on the order</li></ul>
    <p>We may also give your contact details to support services, especially if the respondent (the person the order is about) is taking part in a Court Mandated Counselling Program.</p>`;
  const shareOption3 =
    `<p>We won’t give out any information that could identify you.</p>
    <p>If an ISE asks for information, we will remove (redact) your name and other identifying details. If we can’t remove everything that could identify you, we won’t share the information at all.</p>
    <p><strong>Important:</strong> If we believe there is a serious threat to your safety or the safety of a child, we may still need to share certain information.</p>`;

  return (
    <>
      <Outter>
        <Wrapper>
          <Formik
            isInitialValid={false}
            initialValues={{
              step: 1,
              seeking: "",
              appl_info_share: "",
            }}
            onSubmit={(values) => console.log(values)}
            validationSchema={Yup.object().shape(validation)}
          >
            {({
              values,
              setFieldValue,
              isValid,
              validateForm,
              setTouched,
              errors,
            }) => (
              <>
                {values.step === 1 && (
                  <>
                    <QuestionsFrame>
                      <h1>Your role</h1>
                      <StandaloneRadioGroup
                        name="seeking"
                        label="Select the role that best describes you"
                        options={[
                          {
                            value: "appl",
                            text: "I am an **applicant** or protected person in a family violence intervention order",
                          },
                          {
                            value: "resp",
                            text: "I am the **respondent** in a family violence intervention order",
                          },
                        ]}
                        optionChildren={{
                          appl: (
                            <InfoNote>
                              <p style={{ marginTop: 0 }}>
                                You cannot use this form if you are{" "}
                                <strong>under 18 years old</strong>. You should
                                contact your{" "}
                                <Info
                                  displayText="local court"
                                  headingText="Your local court"
                                  bodyText='<a href="https://www.mcv.vic.gov.au/going-court/find-court" target="_blank" rel="noopener noreferrer" >You can find your nearest court location here.</a>'
                                  inherit
                                />{" "}
                                about extending, varying or revoking an order.
                              </p>
                              <p>
                                You cannot use this form if your original order
                                has expired. Please contact the court or make a
                                new application for an intervention order{" "}
                                <a
                                  href="https://fvio.mcv.vic.gov.au/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: colors.blue,
                                  }}
                                >
                                  here
                                </a>
                                .
                              </p>
                              <p>
                                If your order is due to{" "}
                                <strong>expire in 2 weeks</strong> or less
                                please contact your{" "}
                                <Info
                                  displayText="local court"
                                  headingText="Your local court"
                                  bodyText='<a href="https://www.mcv.vic.gov.au/going-court/find-court" target="_blank" rel="noopener noreferrer" >You can find your nearest court location here.</a>'
                                  inherit
                                />{" "}
                                to apply.
                              </p>
                              <p>
                                If you are an applicant applying on behalf of
                                the Protected Person you will require written
                                consent of a parent of a child Affected Family
                                Member.
                              </p>
                            </InfoNote>
                          ),
                          resp: (
                            <InfoNote>
                              <p style={{ marginTop: 0 }}>
                                You cannot use this form if you are the{" "}
                                <strong>respondent</strong> to an intervention
                                order.
                              </p>

                              <p>
                                You can use DocuSign or submit a form to the
                                court to apply to revoke or vary an order. Go to{" "}
                                <a
                                  href="https://www.mcv.vic.gov.au/intervention-orders/family-violence-intervention-orders/changing-intervention-order-fvio"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: colors.blue,
                                  }}
                                >
                                  Changing an intervention order
                                </a>{" "}
                                for more information.
                              </p>

                              <p>
                                Contact your{" "}
                                <Info
                                  displayText="local court"
                                  headingText="Your local court"
                                  bodyText='<a href="https://www.mcv.vic.gov.au/going-court/find-court" target="_blank" rel="noopener noreferrer" >You can find your nearest court location here.</a>'
                                  inherit
                                />{" "}
                                about varying or revoking an order.
                              </p>
                            </InfoNote>
                          ),
                        }}
                      />
                    </QuestionsFrame>
                    <ButtonContainer>
                      <Button
                        disabled={values.seeking === "resp"}
                        action={() => {
                          setTouched({ seeking: true });
                          validateForm();
                          if (isValid) {
                            setFieldValue("step", 2);
                          }
                        }}
                      >
                        Continue
                      </Button>
                    </ButtonContainer>
                  </>
                )}

                {values.step === 2 && (
                  <>
                    <QuestionsFrame>
                      <h1>Privacy Information Handling statement</h1>
                      <p>
                        The Magistrates Court of Victoria collects, handles and
                        stores personal, sensitive and health information in
                        accordance with the Court's obligations under the
                        Privacy and Data Protection Act 2014, the Health Records
                        Act 2001 and other relevant legislation. Information
                        provided will only be shared where required, authorised
                        or permitted under law.
                      </p>
                      <p>
                        You can find out more information{" "}
                        <a
                          href="https://www.mcv.vic.gov.au/privacy"
                          target="_blank"
                          rel="noreferrer"
                          title="Privacy"
                          style={{ color: colors.blue }}
                        >
                          here
                        </a>
                        .
                      </p>
                    </QuestionsFrame>
                    <QuestionsFrame>
                      <h1>Family Violence Information Sharing Scheme</h1>
                      <p>
                        The Magistrates Court may legally share relevant
                        information with external organisations to assess your
                        risk or manage your ongoing safety. This is through the
                        Family Violence Information Sharing Scheme. Some of
                        these organisations include Child Protection,
                        Corrections, Victoria Police, schools, and other
                        community services. These are known as Information
                        Sharing entities (ISE's).
                      </p>
                      <p style={{ fontSize: "15px" }}>
                        Your information may be shared without your consent if
                        there is a serious threat to yours or another
                        individual's life, health, safety, or welfare. Your
                        information may be shared without consent if it is
                        relevant to assessing or managing risks to a child.
                      </p>
                      <p>
                        You can find out more information{" "}
                        <a
                          href="https://www.mcv.vic.gov.au/privacy"
                          target="_blank"
                          rel="noreferrer"
                          title="Privacy"
                          style={{ color: colors.blue }}
                        >
                          here
                        </a>
                        .
                      </p>
                    </QuestionsFrame>
                    <QuestionsFrame>
                      <StandaloneRadioGroup
                        name="appl_info_share"
                        label="Select one of the following"
                        options={[
                          {
                            value: "full",
                            text: "**I consent** to the sharing of all relevant personal information to ISEs",
                            description: shareOption1
                          },
                          {
                            value: "limited",
                            text: "**I consent to my contact, date of birth and demographic details** being provided to ISEs",
                            description: shareOption2
                          },
                          {
                            value: "no",
                            text: "**I do not consent** to the sharing of personal information to ISEs",
                            description: shareOption3
                          },
                        ]}
                      />
                    </QuestionsFrame>
                    <ButtonContainer>
                      <Button
                        action={() => {
                          setTouched({ appl_info_share: true });
                          infoShareRef.current = values.appl_info_share;
                          validateForm();
                          if (isValid) {
                            setProgress(true);
                          }
                        }}
                      >
                        Continue
                      </Button>
                    </ButtonContainer>
                  </>
                )}
              </>
            )}
          </Formik>
        </Wrapper>
      </Outter>
    </>
  );
};

export default Gatekeeper;
const Outter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: ${rem(52)} 0;
  min-height: calc(100vh - 196px);
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 680px;
`;
